/*
 * drawer-builder-analytics.js
 *
 * Additive, config-driven GA4 funnel instrumentation for the drawer builder.
 * Pushes funnel-step events to window.dataLayer (GTM-M8JVKDS7) for the steps the
 * existing builder did not emit.
 *
 * SAFETY (this runs on a live storefront builder):
 *   - PURELY ADDITIVE. It attaches its own delegated listeners and only READS the
 *     DOM via stable selectors. It never touches the builder's own logic.
 *   - Every push is wrapped in try/catch and guards window.dataLayer, so analytics
 *     can never throw into the builder.
 *   - If a selector is absent (markup changes), the matching step simply no-ops.
 *   - It does NOT emit add_to_cart / begin_checkout / purchase — already tracked
 *     (custom_drawer + the keyless drawer dataLayer at ATC + native checkout/purchase).
 *
 * SCALABILITY — to add a new funnel step, add ONE entry to LOAD_STEPS or
 * CLICK_STEPS below. Nothing else changes here, and on the GTM side the spec uses
 * ONE regex Custom Event trigger + ONE GA4 event tag using {{Event}}, so a new
 * step needs no new tag/trigger either. (e.g. the future "photo-to-build" entry
 * point is just one more entry.)
 *
 * Event/param names are the single source of truth for the GTM trigger regex and
 * GA4 params — keep them in sync with:
 *   marketing-agency/businesses/modudrawer/marketing/drawer-builder-funnel-tracking-spec.md
 *
 * DEBUG: append ?dbAnalytics=1 to the URL (or set window.DB_ANALYTICS_DEBUG = true)
 * to console.log every push — useful while validating in GTM Preview.
 * view_builder also fires on persisted pageshow restores. start_build includes
 * drawer_length, drawer_width, and finish.
 */
(function () {
  'use strict';

  var DEBUG = false;
  try {
    DEBUG = window.DB_ANALYTICS_DEBUG === true ||
      /[?&]dbAnalytics=1\b/.test(window.location.search || '');
  } catch (e) {}

  // ---- primitives -----------------------------------------------------------
  function assign(target, src) {
    if (src) for (var k in src) if (Object.prototype.hasOwnProperty.call(src, k)) target[k] = src[k];
    return target;
  }
  function push(eventName, params) {
    try {
      var payload = assign({ event: eventName }, params);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
      if (DEBUG && window.console) window.console.log('[db-analytics]', payload);
    } catch (e) {
      /* analytics must never break the builder */
    }
  }
  function toInt(v) { var n = parseInt(v, 10); return isFinite(n) ? n : null; }
  function attr(el, name) { return el ? el.getAttribute(name) : null; }
  function text(el) { return el ? (el.textContent || '').trim() : ''; }

  // ---- DOM readers (centralised so selectors live in one place) -------------
  var SEL = {
    length: 'input[name="length"]',
    width: 'input[name="width"]',
    apply: '#changeDimension',
    swatch: '.colorsOption span',
    activeSwatch: '.colorsOption span.active',
    swatchWrap: '.colorsOption',
    tile: '.mineProList',
    placed: '.droppable-area1 .list-view-item',
    placedNew: '.droppable-area1 .list-view-item.recently-added',
    tileTitle: '.proTitle, .product-title, .title, .db-card__title',
    builderMarkers: '.droppable-area1, #changeDimension'
  };
  function readDimensions() {
    return {
      drawer_length: toInt((document.querySelector(SEL.length) || {}).value),
      drawer_width: toInt((document.querySelector(SEL.width) || {}).value)
    };
  }
  function readFinish() {
    try {
      var s = document.querySelector(SEL.activeSwatch);
      return s ? (attr(s, 'data-text') || attr(s, 'title') || attr(s, 'data-id') || '') : '';
    } catch (e) {
      return '';
    }
  }
  function placedCount() { return document.querySelectorAll(SEL.placed).length; }

  // ---- per-session derived state -------------------------------------------
  var state = { buildStarted: false, lastFinishToken: '', viewFired: false };
  function ensureViewBuilder() {
    if (state.viewFired) return;
    state.viewFired = true;
    push('view_builder', null);
  }
  function emitSelectColour() {
    try {
      var s = document.querySelector(SEL.activeSwatch);
      var token = s ? (attr(s, 'data-id') || attr(s, 'data-text') || '') : '';
      if (!token || token === state.lastFinishToken) return;
      state.lastFinishToken = token;
      push('select_colour', { finish: readFinish() });
    } catch (e) {
      /* analytics must never break the builder */
    }
  }
  function markBuildStarted() {
    if (state.buildStarted) return;
    state.buildStarted = true;
    push('start_build', assign(readDimensions(), { finish: readFinish() }));
  }

  // ---- declarative funnel config -------------------------------------------
  // LOAD_STEPS: fired once at init. CLICK_STEPS: fired via delegated click.
  //   selector : delegated click target (closest()).
  //   params(el): returns the event params (sync).
  //   defer(el, fire): optional. Calls fire(extra) later (e.g. after confirming a
  //                    placement actually happened). When present, params(el, extra).
  var LOAD_STEPS = [
    { event: 'view_builder', fire: ensureViewBuilder }
  ];

  var CLICK_STEPS = [
    {
      event: 'place_module',
      selector: SEL.tile,
      // Only count a placement that actually landed (the grid rejects when full).
      defer: function (el, fire) {
        var before = placedCount();
        setTimeout(function () {
          try {
            var after = placedCount();
            if (after <= before) return;
            var nodes = document.querySelectorAll(SEL.placedNew);
            var placedEl = nodes.length ? nodes[nodes.length - 1] : null;
            markBuildStarted();
            fire({ count: after, placedEl: placedEl });
          } catch (e) {
            /* analytics must never break the builder */
          }
        }, 150);
      },
      params: function (el, extra) {
        var gw = attr(el, 'grid-width'), gl = attr(el, 'grid-length');
        var placedEl = extra && extra.placedEl;
        var id = attr(el, 'product-id') || attr(el, 'data-id') || attr(el, 'itemid') ||
          (placedEl ? attr(placedEl, 'product-id') : '') || '';
        id = String(id || '');
        var titleEl = el.querySelector(SEL.tileTitle);
        var moduleName = attr(el, 'data-title') || attr(el, 'title') || text(titleEl) || '';
        if (!moduleName && id) {
          try {
            var products = window.products || [];
            for (var i = 0; i < products.length; i++) {
              if (products[i] && String(products[i].id) === String(id)) {
                moduleName = products[i].title || '';
                break;
              }
            }
          } catch (e) {}
        }
        if ((!gw || !gl) && placedEl) {
          gw = attr(placedEl, 'pro-width');
          gl = attr(placedEl, 'pro-length');
        }
        return {
          module_id: String(id || ''),
          module_name: moduleName,
          module_cells: (gw && gl) ? gw + 'x' + gl : '',
          module_count: extra && extra.count
        };
      }
    }
  ];

  // ---- dispatcher -----------------------------------------------------------
  function handleClick(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    ensureViewBuilder();
    if (t.closest(SEL.swatch)) {
      setTimeout(function () {
        try { emitSelectColour(); } catch (err) {}
      }, 0);
    }
    for (var i = 0; i < CLICK_STEPS.length; i++) {
      var step = CLICK_STEPS[i];
      var el = t.closest(step.selector);
      if (!el) continue;
      if (typeof step.defer === 'function') {
        (function (step, el) {
          step.defer(el, function (extra) { push(step.event, step.params(el, extra)); });
        })(step, el);
      } else {
        push(step.event, step.params ? step.params(el) : null);
      }
      return; // at most one step per click
    }
  }

  function isBuilderPage() {
    if (document.body && document.body.classList.contains('drawer-builder')) return true;
    return !!document.querySelector(SEL.builderMarkers);
  }

  function init() {
    if (!isBuilderPage()) return;
    for (var i = 0; i < LOAD_STEPS.length; i++) {
      if (typeof LOAD_STEPS[i].fire === 'function') LOAD_STEPS[i].fire();
      else push(LOAD_STEPS[i].event, LOAD_STEPS[i].params ? LOAD_STEPS[i].params() : null);
    }
    try {
      var active = document.querySelector(SEL.activeSwatch);
      state.lastFinishToken = active ? (attr(active, 'data-id') || attr(active, 'data-text') || '') : '';
      var swatchWrap = document.querySelector(SEL.swatchWrap);
      if (window.MutationObserver && swatchWrap) {
        new window.MutationObserver(function () {
          try { emitSelectColour(); } catch (err) {}
        }).observe(swatchWrap, { attributes: true, subtree: true, attributeFilter: ['class'] });
      }
    } catch (e) {
      /* analytics must never break the builder */
    }
    document.addEventListener('click', handleClick, true); // capture phase
    window.addEventListener('db:dimensions-applied', function (event) {
      try {
        var detail = event && event.detail ? event.detail : {};
        push('set_dimensions', {
          drawer_length: toInt(detail.drawer_length),
          drawer_width: toInt(detail.drawer_width),
          drawer_target_height_mm: toInt(detail.drawer_target_height_mm)
        });
      } catch (e) {
        /* analytics must never break the builder */
      }
    });
  }

  window.addEventListener('pageshow', function (e) {
    try {
      if (e && e.persisted && isBuilderPage()) {
        state.viewFired = false;
        ensureViewBuilder();
      }
    } catch (err) {}
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
